import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { ExpiryOpenInterestProto } from '../../proto/generated/open-interest'
import { Exchange } from '../../proto/generated/common'
import { TradingSession } from '../../proto/generated/trading-calendar'
import { Security } from '../../proto/generated/security'
import { selectTradingCalendarDataByExchange } from '../../selectors/chart'
import { selectOiStrikeFilterLtp } from '../../selectors/security'
import { getSecurityKey } from '../../util/security'
import { getEffectiveTradingDate } from '../../util/tradingCalendar'
import { resolveTradingSessionsFromCalendar } from '../../util/tradingSessionSelection'
import { type OIAnalysisSettings } from '../../reducer/multiStrikeChart.reducer'
import { OpenInterestChartSettings } from '../../components/chartV2/chart.typings'
import { buildOiChartData, filterStrikesAroundLtp, OiChartRow } from './oiChartData'
import {
    expiryIdsFromSnapshot,
    fetchOiSnapshot,
    oiSnapshotMemoryKey,
    readOiSnapshotMemory,
    resolveOiCustomRangeWindow,
    resolveOiWindow,
} from './oiAnalysisOiData'

export type OiAnalysisSettingsLike = OpenInterestChartSettings &
    Pick<OIAnalysisSettings, 'rangeType' | 'customFromDate' | 'customToDate'>

function buildMinuteMap(sessions: number[][], currentTime: number): number[] {
    const minutes: number[] = []

    for (const session of sessions) {
        const sessionStart = session[0]
        const sessionEnd = session[1]
        const effectiveEnd =
            currentTime >= sessionStart && currentTime <= sessionEnd ? currentTime : sessionEnd

        for (let t = sessionStart; t <= effectiveEnd; t += 60_000) {
            minutes.push(t)
        }
        if (effectiveEnd === currentTime && currentTime < sessionEnd) {
            break
        }
    }

    return minutes
}

function computeVisibleOiTotals(
    oiChangeData: ExpiryOpenInterestProto[],
    selectedExpiries: string[],
    strikeCount: string,
    stableLastTradedPrice: number
) {
    const filtered =
        selectedExpiries.length > 0
            ? oiChangeData.filter((e) => selectedExpiries.includes(e.expiry.toString()))
            : oiChangeData

    const byStrike: Record<
        number,
        { callFrom: number; callTo: number; putFrom: number; putTo: number }
    > = {}

    filtered.forEach((expiryWiseData) => {
        expiryWiseData.strikeOptionChainLineItem.forEach((strikeData) => {
            const sp = strikeData.strikePrice
            const cur = byStrike[sp] ?? { callFrom: 0, callTo: 0, putFrom: 0, putTo: 0 }
            cur.callFrom += strikeData?.from?.callOi ?? 0
            cur.callTo += strikeData?.to?.callOi ?? 0
            cur.putFrom += strikeData?.from?.putOi ?? 0
            cur.putTo += strikeData?.to?.putOi ?? 0
            byStrike[sp] = cur
        })
    })

    const allStrikes = Object.keys(byStrike).map(Number)
    const visible = filterStrikesAroundLtp(allStrikes, strikeCount, stableLastTradedPrice)

    let totalCallOi = 0
    let totalPutOi = 0
    let callOiChange = 0
    let putOiChange = 0

    visible.forEach((sp) => {
        const row = byStrike[sp]
        if (!row) return
        totalCallOi += row.callTo
        totalPutOi += row.putTo
        callOiChange += row.callTo - row.callFrom
        putOiChange += row.putTo - row.putFrom
    })

    return { totalCallOi, totalPutOi, callOiChange, putOiChange }
}

export type UseOiAnalysisChartDataArgs = {
    security: Security | undefined
    settings: OiAnalysisSettingsLike
    onSettingsPatch: (patch: Partial<OIAnalysisSettings>) => void
    isOIChangeScreen?: boolean
    /** Namespace for in-memory snapshot cache (e.g. viewKey or 'openInterest'). */
    snapshotNamespace: string
}

export type UseOiAnalysisChartDataResult = {
    chartData: OiChartRow[]
    loading: boolean
    minuteMap: number[]
    value: [number, number]
    onSliderChange: (_: unknown, v: number | number[]) => void
    onSliderCommit: (_: unknown, v: number | number[]) => void
    onQuickSelect: (minutes: number | 'full') => void
    showTimeSlider: boolean
    footerDateLabel: string
    totalCallOi: number
    totalPutOi: number
    callOiChange: number
    putOiChange: number
    showOI: boolean
    replayKey: number
}

export function useOiAnalysisChartData({
    security,
    settings,
    onSettingsPatch,
    isOIChangeScreen = false,
    snapshotNamespace,
}: UseOiAnalysisChartDataArgs): UseOiAnalysisChartDataResult {
    const [oiChangeData, setOiChangeData] = useState<ExpiryOpenInterestProto[]>([])
    const calendarEvents = useSelector(
        selectTradingCalendarDataByExchange(security?.exchange ?? Exchange.NSE)
    )

    const effectiveTradingDate = useMemo(() => getEffectiveTradingDate(calendarEvents), [calendarEvents])

    const [value, setValue] = useState<[number, number]>([0, 0])
    const [apiValue, setApiValue] = useState<[number, number]>([0, 0])
    const [isInitialized, setIsInitialized] = useState(false)
    const [loading, setLoading] = useState(false)

    const showOI = settings.showOI ?? false
    const replayKey = settings.replayKey ?? 0
    const strikeCount = settings.strikeCount ?? '25'
    const selectedExpiries = settings.selectedExpiries ?? []
    const settingsSecurityKey = settings.securityKey
    const expiriesMenuFromApi = settings.expiriesMenuFromApi ?? []
    const expiriesMenuLoading = settings.expiriesMenuLoading ?? false
    const selectedDate = settings.selectedDate
    const rangeType = settings.rangeType
    const customFromDate = settings.customFromDate
    const customToDate = settings.customToDate
    const timeWindowMinutes = settings.timeWindowMinutes

    const securityKey = useMemo(
        () => (security ? getSecurityKey(security.exchange, security.code) : ''),
        [security]
    )
    const strikeFilterLtp = Number(useSelector(selectOiStrikeFilterLtp(securityKey)) ?? 0)

    const snapshotMemoryKey = useMemo(
        () => oiSnapshotMemoryKey(snapshotNamespace, securityKey),
        [snapshotNamespace, securityKey]
    )

    useEffect(() => {
        const remembered = readOiSnapshotMemory(snapshotMemoryKey)
        if (remembered) setOiChangeData(remembered)
    }, [snapshotMemoryKey])

    useEffect(() => {
        if (securityKey && settingsSecurityKey !== securityKey) {
            onSettingsPatch({
                selectedExpiries: [],
                securityKey,
                expiriesMenuFromApi: [],
                expiriesMenuLoading: true,
            })
        }
    }, [securityKey, settingsSecurityKey, onSettingsPatch])

    const req = useRef<{ from: number; to: number; security: Security }>({
        from: null,
        to: null,
        security: null,
    })
    const isDateChanging = useRef(false)

    const getSessionStartEnd = useCallback((session: TradingSession, baseDate: dayjs.Dayjs) => {
        const start = baseDate.startOf('day').add(session?.startTime, 'second')
        const end = baseDate.startOf('day').add(session?.endTime, 'second')
        return { start, end }
    }, [])

    const getSessionHoursForDate = useCallback(
        (date: dayjs.Dayjs) => {
            const sessionsForToday = resolveTradingSessionsFromCalendar(
                calendarEvents,
                date.toDate(),
                security
            )
            const allSessionHours: number[][] = []
            sessionsForToday.forEach((session: TradingSession) => {
                const { start, end } = getSessionStartEnd(session, date)
                allSessionHours.push([start.unix() * 1000, end.unix() * 1000])
            })
            return allSessionHours
        },
        [calendarEvents, security, getSessionStartEnd]
    )

    const minuteMap = useMemo(() => {
        const dateToUse = selectedDate != null ? new Date(selectedDate) : effectiveTradingDate

        const today = new Date()
        const isViewingToday = dayjs(dateToUse).isSame(dayjs(today), 'day')
        const isLiveTradingSessionDay = dayjs(today).isSame(dayjs(effectiveTradingDate), 'day')

        const effectiveTime =
            isViewingToday && isLiveTradingSessionDay
                ? dayjs().startOf('minute').valueOf()
                : dayjs(dateToUse).endOf('day').valueOf()
        return buildMinuteMap(getSessionHoursForDate(dayjs(dateToUse)), effectiveTime)
    }, [getSessionHoursForDate, effectiveTradingDate, selectedDate])

    const applyTimeWindow = useCallback(
        (minutes: number | 'full' | undefined) => {
            if (minuteMap.length === 0) return

            const lastIndex = minuteMap.length - 1
            if (minutes == null || minutes === 'full') {
                setValue([0, lastIndex])
                setApiValue([0, lastIndex])
                return
            }
            const targetIndex = Math.max(0, lastIndex - minutes)
            setValue([targetIndex, lastIndex])
            setApiValue([targetIndex, lastIndex])
        },
        [minuteMap]
    )

    const handleQuickSelect = useCallback(
        (minutes: number | 'full') => {
            applyTimeWindow(minutes)
        },
        [applyTimeWindow]
    )

    const isSelectedDateToday = useMemo(() => {
        const analysisDate = selectedDate != null ? dayjs(selectedDate) : dayjs(effectiveTradingDate)
        return analysisDate.isSame(dayjs(), 'day')
    }, [selectedDate, effectiveTradingDate])

    const visibleOiFooterTotals = useMemo(
        () =>
            computeVisibleOiTotals(
                oiChangeData,
                selectedExpiries ?? [],
                strikeCount ?? '25',
                strikeFilterLtp
            ),
        [oiChangeData, selectedExpiries, strikeCount, strikeFilterLtp]
    )

    useEffect(() => {
        setIsInitialized(false)
    }, [isOIChangeScreen, selectedDate, rangeType, customFromDate, customToDate])

    useEffect(() => {
        if (isInitialized) {
            return
        }

        if (rangeType === 'customRange') {
            setIsInitialized(true)
        } else if (minuteMap.length > 0) {
            applyTimeWindow(timeWindowMinutes)
            setIsInitialized(true)
        } else {
            const today = new Date()
            const isTodayTradingDay = dayjs(today).isSame(dayjs(effectiveTradingDate), 'day')
            if (!isTodayTradingDay) {
                setIsInitialized(true)
            }
        }
    }, [minuteMap, isInitialized, effectiveTradingDate, rangeType, applyTimeWindow, timeWindowMinutes])

    useEffect(() => {
        if (!isInitialized || timeWindowMinutes == null) return
        applyTimeWindow(timeWindowMinutes)
    }, [timeWindowMinutes, isInitialized, applyTimeWindow])

    const fetchOpenInterestData = useCallback(async () => {
        if (!securityKey || !security || settingsSecurityKey !== securityKey) {
            return
        }

        if (loading) {
            return
        }

        setLoading(true)
        const expiriesArray = selectedExpiries.length > 0 ? selectedExpiries.map((e) => Number(e)) : []
        const requestingAllExpiries = expiriesArray.length === 0
        const prevExpiriesMenu = expiriesMenuFromApi ?? []

        onSettingsPatch({ expiriesMenuLoading: true })

        const analysisDay = selectedDate != null ? dayjs(selectedDate) : dayjs(effectiveTradingDate)
        const isCustomRange =
            isOIChangeScreen && rangeType === 'customRange' && !!customFromDate && !!customToDate

        const { fromTime, toTime } = isCustomRange
            ? resolveOiCustomRangeWindow(
                  dayjs(customFromDate),
                  dayjs(customToDate),
                  getSessionHoursForDate(dayjs(customFromDate)),
                  getSessionHoursForDate(dayjs(customToDate))
              )
            : resolveOiWindow({
                  analysisDay,
                  effectiveTradingDate,
                  sessionHours: isOIChangeScreen ? [] : getSessionHoursForDate(analysisDay),
                  sliderWindow: req.current,
                  dayBounded: isOIChangeScreen,
              })

        if (!fromTime || !toTime) {
            onSettingsPatch({ expiriesMenuLoading: false })
            setLoading(false)
            return
        }

        const sortExpiryIds = (a: string, b: string) => Number(a) - Number(b)

        try {
            const rows = await fetchOiSnapshot({
                security: {
                    code: security.code,
                    exchange: security.exchange,
                },
                expiries: expiriesArray,
                window: { fromTime, toTime },
                memoryKey: snapshotMemoryKey,
            })

            setOiChangeData(rows)

            const { all: sortedFromResponse, withChain: sortedSelectedFromResponse } =
                expiryIdsFromSnapshot(rows)
            const nextExpiriesMenuFromApi = requestingAllExpiries
                ? sortedFromResponse
                : [...new Set([...prevExpiriesMenu, ...sortedFromResponse])].sort(sortExpiryIds)

            const patch: Partial<OIAnalysisSettings> = {
                expiriesMenuFromApi: nextExpiriesMenuFromApi,
                expiriesMenuLoading: false,
                securityKey,
            }
            if (sortedSelectedFromResponse.length > 0 && requestingAllExpiries) {
                patch.selectedExpiries = sortedSelectedFromResponse
            }

            onSettingsPatch(patch)
        } catch {
            onSettingsPatch({ expiriesMenuLoading: false })
        } finally {
            setLoading(false)
        }
    }, [
        securityKey,
        settingsSecurityKey,
        selectedExpiries,
        expiriesMenuFromApi,
        rangeType,
        customFromDate,
        customToDate,
        selectedDate,
        isOIChangeScreen,
        onSettingsPatch,
        security,
        effectiveTradingDate,
        getSessionHoursForDate,
        snapshotMemoryKey,
        loading,
    ])

    useEffect(() => {
        if (expiriesMenuLoading && expiriesMenuFromApi.length === 0) {
            isDateChanging.current = true
        } else if (!expiriesMenuLoading) {
            setTimeout(() => {
                isDateChanging.current = false
            }, 100)
        }
    }, [expiriesMenuLoading, expiriesMenuFromApi.length])

    useEffect(() => {
        if (isInitialized && security) {
            if (rangeType === 'customRange') {
                fetchOpenInterestData()
            } else if (minuteMap.length > 0) {
                req.current = {
                    from: Math.min(minuteMap[apiValue[0]], minuteMap[apiValue[1]]),
                    to: Math.max(minuteMap[apiValue[0]], minuteMap[apiValue[1]]),
                    security,
                }
                fetchOpenInterestData()
            } else if (minuteMap.length === 0) {
                fetchOpenInterestData()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        security,
        apiValue[0],
        apiValue[1],
        isInitialized,
        effectiveTradingDate,
        selectedDate,
        rangeType,
        customFromDate,
        customToDate,
    ])

    useEffect(() => {
        if (
            isInitialized &&
            security &&
            selectedExpiries &&
            !expiriesMenuLoading &&
            !isDateChanging.current
        ) {
            if (minuteMap.length > 0) {
                req.current = {
                    from: Math.min(minuteMap[apiValue[0]], minuteMap[apiValue[1]]),
                    to: Math.max(minuteMap[apiValue[0]], minuteMap[apiValue[1]]),
                    security,
                }
            }
            fetchOpenInterestData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedExpiries])

    const priceMultiplier = security?.priceMultiplier ?? 100

    const chartData = useMemo(
        () =>
            buildOiChartData({
                oiChangeData,
                selectedExpiries,
                strikeCount: strikeCount ?? '25',
                strikeFilterLtp,
                priceMultiplier,
                showOI,
                isOIChangeScreen,
            }),
        [
            oiChangeData,
            selectedExpiries,
            strikeCount,
            strikeFilterLtp,
            priceMultiplier,
            showOI,
            isOIChangeScreen,
        ]
    )

    const footerDateLabel = useMemo(
        () =>
            (selectedDate != null ? dayjs(selectedDate) : dayjs(effectiveTradingDate)).format(
                'ddd, DD MMM'
            ),
        [selectedDate, effectiveTradingDate]
    )

    const showTimeSlider = useMemo(
        () =>
            minuteMap.length > 0 &&
            ((!isOIChangeScreen && isSelectedDateToday) ||
                (isOIChangeScreen && (rangeType ?? 'intraday') === 'intraday')),
        [minuteMap.length, isOIChangeScreen, isSelectedDateToday, rangeType]
    )

    const onSliderChange = useCallback((_: unknown, v: number | number[]) => {
        setValue([...(v as [number, number])])
    }, [])

    const onSliderCommit = useCallback((_: unknown, v: number | number[]) => {
        setApiValue([...(v as [number, number])])
    }, [])

    return {
        chartData,
        loading,
        minuteMap,
        value,
        onSliderChange,
        onSliderCommit,
        onQuickSelect: handleQuickSelect,
        showTimeSlider,
        footerDateLabel,
        totalCallOi: visibleOiFooterTotals.totalCallOi,
        totalPutOi: visibleOiFooterTotals.totalPutOi,
        callOiChange: visibleOiFooterTotals.callOiChange,
        putOiChange: visibleOiFooterTotals.putOiChange,
        showOI,
        replayKey,
    }
}
